/**
 * Tab 2 — Abby welcome (spec 2026-10-23 §4.2, §5). What a new member sees and
 * hears the first time they open the Abby tab.
 *
 * Content (mode, text, Abby's voice, videos) is a DRAFT until Publish. On/off
 * is the switch, which publishes the newest version with `enabled` flipped.
 * Nothing published = ON with the built-in text and voice (spec §4.2).
 *
 * Every change that reaches members goes through ConfirmDialog first, and every
 * publish sends the newest version this page has seen (`based_on_version`), so
 * a change made by someone else is a 409, never an overwrite. Before anything
 * is sent, the page runs the SAME validator as the server, with the server's
 * media origin (R19). Publish sends the voice that assemble RETURNED, never the
 * draft. The rules live in lib/welcome-state.ts; this file wires them.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from '@/hooks/useToast';
import type { AdminVersionRow } from './api';
import { ConfirmDialog } from './components/ConfirmDialog';
import { HistoryList } from './components/HistoryList';
import { IntroSwitch, StatusChip } from './components/IntroSwitch';
import { LanguagePanel } from './components/LanguagePanel';
import { ParagraphListEditor } from './components/ParagraphListEditor';
import { PublishBar } from './components/PublishBar';
import { SyncedPreview } from './components/SyncedPreview';
import { VoiceGenerator } from './components/VoiceGenerator';
import { WelcomeModeField } from './components/WelcomeModeField';
import type { ConfirmRequest } from './lib/confirm-requests';
import { validateAbbyWelcomeConfig, type AbbyWelcomeConfig, type ExperienceLocale, type WelcomeVoiceLocale } from './lib/contract';
import { LANGUAGE_NAME, LOCALES, type FinalizedMedia } from './lib/intro-state';
import {
  welcomeDiscardRequest,
  welcomePublishRequest,
  welcomeResetRequest,
  welcomeRestoreRequest,
  welcomeSwitchRequest,
} from './lib/welcome-confirm';
import {
  NOTE_FOR_VIDEO_MODE,
  cannotPublishText,
  generatedFrom,
  voiceForDraft,
  voiceMatches,
  voicePlan,
  welcomeConfigOf,
  welcomeDraftFrom,
  welcomeHistorySummary,
  welcomeNowText,
  welcomePosterOf,
  welcomeRestorePlan,
  welcomeStatusOf,
  welcomeSwitchState,
  welcomeTabState,
  type WelcomeDraft,
} from './lib/welcome-state';
import { builtInText, isBuiltInText } from './lib/welcome-text';
import { useLeaveWarning, type AppExperienceData } from './useAppExperience';
import { useVoiceGeneration } from './useVoiceGeneration';

const ID = 'welcome';

type Action =
  | { kind: 'publish'; config: AbbyWelcomeConfig; basedOn: number | null; resetDraft: boolean }
  | { kind: 'discard' }
  | { kind: 'reset'; locale: ExperienceLocale };

interface Pending {
  request: ConfirmRequest;
  action: Action;
}

interface Props {
  data: AppExperienceData;
  rows: AdminVersionRow[];
  /** `media_origin` of the admin view: the server checks every media URL against it. */
  mediaOrigin: string;
  canWrite: boolean;
}

export function WelcomeTab({ data, rows, mediaOrigin, canWrite }: Props) {
  const latest = rows[0] ?? null;
  const live = useMemo(() => (latest ? welcomeConfigOf(latest.config) : null), [latest]);
  const latestVersion = latest?.version ?? null;

  // null = no local edit: the draft follows the newest published version.
  const [edited, setEdited] = useState<WelcomeDraft | null>(null);
  const draft = edited ?? welcomeDraftFrom(live);
  // The voice assemble returned in this page, per language. The voice that counts is the one
  // that matches the draft — made here or published (voiceForDraft).
  const [madeHere, setMadeHere] = useState<Partial<Record<ExperienceLocale, WelcomeVoiceLocale>>>({});
  const generated = voiceForDraft(draft, madeHere, generatedFrom(live));

  const onReady = useCallback((locale: ExperienceLocale, voice: WelcomeVoiceLocale) => {
    setMadeHere((m) => ({ ...m, [locale]: voice }));
  }, []);
  const gen = useVoiceGeneration(data.apiUrl, onReady);

  const tab = welcomeTabState({ live, draft, generated, canWrite, mediaOrigin });
  const plan = voicePlan(draft, generated);
  const sw = welcomeSwitchState({ live, canWrite });
  const liveOn = sw.on;

  const [uploading, setUploading] = useState<Record<ExperienceLocale, boolean>>({ en: false, es: false });
  const anyUpload = uploading.en || uploading.es;
  useLeaveWarning(tab.dirty || gen.running || anyUpload);

  const [pending, setPending] = useState<Pending | null>(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const statusHeading = useRef<HTMLHeadingElement>(null);
  /** Where focus goes when the dialog closes after a success: 'status' (the status heading) or an element id. */
  const focusOnClose = useRef<string | null>(null);

  const edit = (change: (d: WelcomeDraft) => WelcomeDraft) => setEdited((prev) => change(prev ?? welcomeDraftFrom(live)));
  const setText = (loc: ExperienceLocale) => (paragraphs: string[]) => edit((d) => ({ ...d, text: { ...d.text, [loc]: paragraphs } }));
  const setMedia = (loc: ExperienceLocale) => (media: FinalizedMedia | null) => edit((d) => ({ ...d, videos: { ...d.videos, [loc]: media } }));
  const busyEn = useCallback((b: boolean) => setUploading((u) => ({ ...u, en: b })), []);
  const busyEs = useCallback((b: boolean) => setUploading((u) => ({ ...u, es: b })), []);

  /** Back to the newest published version: draft, voice made here and progress rows. */
  const forgetDraft = () => {
    setEdited(null);
    setMadeHere({});
    gen.reset();
  };

  const open = (request: ConfirmRequest, action: Action) => {
    setNote('');
    setActionError(null);
    setPending({ request, action });
  };
  const close = () => setPending(null);

  const openSwitch = () => open(welcomeSwitchRequest(sw, tab.dirty), { kind: 'publish', config: sw.next, basedOn: latestVersion, resetDraft: false });
  const openPublish = () => {
    if (!tab.config) return;
    open(
      welcomePublishRequest({
        mode: draft.mode,
        liveOn,
        warnings: tab.warnings,
        textNotSent: tab.textNotSent,
        customVoice: tab.config.mode === 'voice' && tab.config.voice !== null,
      }),
      { kind: 'publish', config: tab.config, basedOn: latestVersion, resetDraft: true },
    );
  };
  const openRestore = (row: AdminVersionRow, config: AbbyWelcomeConfig) => {
    if (latestVersion === null) return;
    const plan = welcomeRestorePlan({ config, latestVersion, live, dirty: tab.dirty });
    open(welcomeRestoreRequest(row.version, plan, liveOn), { kind: 'publish', config: plan.config, basedOn: plan.based_on_version, resetDraft: true });
  };
  const openDiscard = () => open(welcomeDiscardRequest(), { kind: 'discard' });
  const openReset = (loc: ExperienceLocale) => open(welcomeResetRequest(loc), { kind: 'reset', locale: loc });

  const generate = () => {
    if (plan.blocked !== null || plan.locales.length === 0) return;
    const input: Partial<Record<ExperienceLocale, string[]>> = {};
    for (const loc of plan.locales) input[loc] = draft.text[loc];
    void gen.start(input);
  };

  const confirm = async () => {
    if (!pending) return;
    const { action, request } = pending;
    if (action.kind === 'discard' || action.kind === 'reset') {
      if (action.kind === 'discard') {
        forgetDraft();
        focusOnClose.current = 'status';
      } else {
        setText(action.locale)(builtInText(action.locale));
        // The Reset button is disabled now; focus goes to the first paragraph of that text.
        focusOnClose.current = `${ID}-${action.locale}-p1`;
      }
      close();
      toast({ title: request.success });
      return;
    }
    // The server's own check, here first, so a refusal is in plain words and nothing is sent.
    const checked = validateAbbyWelcomeConfig(action.config, mediaOrigin);
    if (!checked.ok) {
      setActionError(cannotPublishText(action.config));
      return;
    }
    setSubmitting(true);
    setActionError(null);
    const text = note.trim();
    const r = await data.publish('abby_welcome', { config: action.config, based_on_version: action.basedOn, note: text === '' ? null : text });
    setSubmitting(false);
    if (r.ok) {
      if (action.resetDraft) forgetDraft();
      focusOnClose.current = 'status';
      close();
      toast({ title: request.success, description: `This is version ${r.version}.`, variant: 'success' });
    } else if (r.conflict) {
      close();
    } else {
      setActionError(r.message);
    }
  };

  const busy = submitting || anyUpload || gen.running;
  const waitReason = anyUpload
    ? 'Wait until the upload is finished. Then publish.'
    : gen.running
      ? "Wait until Abby's voice is ready. Then publish."
      : null;
  const effect = liveOn
    ? 'The Abby welcome is on. When you publish, new members get this version.'
    : 'The Abby welcome is off. Publish saves this version. Members do not see it until you turn the welcome on.';
  const isVideo = draft.mode === 'video';
  // Built-in text in both languages: members hear the built-in voice, which is in the app. No preview here.
  const bothBuiltIn = LOCALES.every((loc) => isBuiltInText(draft.text[loc], loc));
  const voiceCurrent = !bothBuiltIn && LOCALES.every((loc) => voiceMatches(generated[loc], draft.text[loc]));

  return (
    <div className="space-y-6">
      <section className="space-y-4 rounded-lg border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <h3 ref={statusHeading} tabIndex={-1} className="flex items-center gap-2 text-lg font-semibold focus:outline-none">
              Abby welcome <StatusChip status={welcomeStatusOf(live)} />
            </h3>
            <p className="text-sm text-muted-foreground">
              What a new member sees and hears the first time they open the Abby tab. It plays one time for each member.
            </p>
            <p className="text-xs text-muted-foreground">{welcomeNowText(live)}</p>
          </div>
          <IntroSwitch sw={sw} disabled={submitting} onToggle={openSwitch} label="Show the Abby welcome to new members" idPrefix={ID} />
        </div>
      </section>

      <WelcomeModeField value={draft.mode} disabled={!canWrite} onChange={(mode) => edit((d) => ({ ...d, mode }))} idPrefix={ID} />

      {/* Both parts stay mounted (hidden, not removed): switching the mode must not stop an upload or a voice run. */}
      <div hidden={isVideo} className="space-y-6">
        <div className="grid gap-6 xl:grid-cols-2">
          {LOCALES.map((loc) => (
            <ParagraphListEditor
              key={loc}
              locale={loc}
              paragraphs={draft.text[loc]}
              builtIn={isBuiltInText(draft.text[loc], loc)}
              disabled={!canWrite}
              onChange={setText(loc)}
              onReset={() => openReset(loc)}
              idPrefix={ID}
            />
          ))}
        </div>
        <VoiceGenerator
          plan={plan}
          snapshot={gen.snapshot}
          disabled={!canWrite}
          onGenerate={generate}
          onRetry={(loc, i) => void gen.retry(loc, i)}
          onRetryJoin={(loc) => void gen.retryJoin(loc)}
          voiceCurrent={voiceCurrent}
          idPrefix={ID}
        />
        <div className="grid gap-6 xl:grid-cols-2">
          {LOCALES.map((loc) => {
            const voice = generated[loc];
            return voice && !bothBuiltIn ? (
              <SyncedPreview
                key={`${loc}-${voice.audio_url}`}
                voice={voice}
                languageName={LANGUAGE_NAME[loc]}
                stale={!voiceMatches(voice, draft.text[loc])}
                idPrefix={ID}
              />
            ) : null;
          })}
        </div>
      </div>

      <div hidden={!isVideo} className="space-y-6">
        <p className="rounded-md border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-100">
          {NOTE_FOR_VIDEO_MODE}
        </p>
        <div className="grid gap-6 xl:grid-cols-2">
          {LOCALES.map((loc) => (
            <LanguagePanel
              key={loc}
              locale={loc}
              apiUrl={data.apiUrl}
              media={draft.videos[loc]}
              liveUrl={live?.videos[loc]?.url ?? null}
              description={draft.descriptions[loc]}
              canWrite={canWrite}
              onMedia={setMedia(loc)}
              onDescription={(text) => edit((d) => ({ ...d, descriptions: { ...d.descriptions, [loc]: text } }))}
              onBusyChange={loc === 'en' ? busyEn : busyEs}
              idPrefix={`app-experience-${ID}`}
            />
          ))}
        </div>
      </div>

      <PublishBar
        state={tab}
        hasLive={live !== null}
        liveOn={liveOn}
        canWrite={canWrite}
        busy={busy}
        waitReason={waitReason}
        onPublish={openPublish}
        onDiscard={openDiscard}
        idPrefix={ID}
        effect={effect}
      />

      <HistoryList
        rows={rows}
        whoName={data.whoName}
        canWrite={canWrite}
        busy={busy}
        parse={welcomeConfigOf}
        summarize={welcomeHistorySummary}
        posterOf={welcomePosterOf}
        onRestore={openRestore}
        idPrefix={ID}
      />

      <ConfirmDialog
        request={pending?.request ?? null}
        note={note}
        onNoteChange={setNote}
        submitting={submitting}
        error={actionError}
        onCancel={close}
        onConfirm={() => void confirm()}
        onCloseAutoFocus={(e) => {
          const target = focusOnClose.current;
          if (target === null) return;
          focusOnClose.current = null;
          e.preventDefault();
          if (target === 'status') statusHeading.current?.focus();
          else document.getElementById(target)?.focus();
        }}
      />
    </div>
  );
}
